/**
 * GameScene - playable game view.
 *
 * Step 7 changes:
 *   - Accepts a RunConfig from MenuScene (init(config) hook)
 *   - Modes: daily, adventure, quick
 *   - Adventure: 3 lives, 7 levels with per-level scroll/spider/water scaling
 *   - Lives HUD when applicable; level indicator for adventure
 *   - Esc returns to menu
 *   - On death: adventure loses a life and restarts the current level (or
 *     ends the run if 0 lives left); daily/quick show result overlay
 *   - On win: adventure advances to next level (or shows final summary on L7);
 *     daily/quick show result overlay
 */

import Phaser from 'phaser';
import {
  PLAYFIELD_TILES_W,
  PLAYFIELD_TILES_H,
  VIEWPORT_TILES_W,
  VIEWPORT_TILES_H,
  VIEWPORT_PX_W,
  VIEWPORT_PX_H,
  TILE_SIZE,
} from '../config';
import { generatePlayfield } from '../maze/generator';
import { Tile, type Playfield } from '../maze/types';
import { ScrollController } from '../scroll/ScrollController';
import { InputController } from '../input/InputController';
import { Chimp } from '../game/Chimp';
import { SpiderManager } from '../game/SpiderManager';
import { Score, formatTime, type ScoreSummary } from '../game/Score';
import { TOTAL_ADVENTURE_LEVELS, levelConfig } from '../game/Mode';
import type { RunConfig } from '../game/RunConfig';
import {
  load,
  commit,
  recordDailyAttempt,
  recordAdventureRun,
  computeStreak,
  type SaveState,
} from '../persistence/saveState';
import { buildShareText } from '../share/shareFormat';
import { copyToClipboard } from '../share/clipboard';
import { AssetManager } from '../assets/AssetManager';
import { SoundManager } from '../sound/SoundManager';
import { HapticsManager } from '../haptics/HapticsManager';
import { ParentBridge, type Pausable } from '../bridge/ParentBridge';

const C = {
  ground: 0x2f4422,
  groundAlt: 0x29401e,
  foliage: 0x0a1c08,
  foliageEdge: 0x142a10,
  water: 0x1c4a7a,
  waterEdge: 0x2a6090,
  spider: 0x111111,
  spiderPatrol: 0x35351a,
  coconut: 0xb37a3a,
  coconutShine: 0xe8a55a,
  coconutInvisible: 0x6b3d20,
  telegraph: 0xffd966,
  telegraphGlow: 0xfff2b0,
} as const;

const CAMERA_TWEEN_MS = 120;
const VIEWPORT_DEATH_MARGIN = 0;
const OVERLAY_DELAY_MS = 350;

/** Base spider tick interval — multiplied by config's spiderTickMul. */
const SPIDER_BASE_TICK_MS = 800;

export class GameScene extends Phaser.Scene implements Pausable {
  private cfg!: RunConfig;
  private playfield!: Playfield;
  private scroll!: ScrollController;
  private input_!: InputController;
  private chimp!: Chimp;
  private spiders!: SpiderManager;
  private score!: Score;
  private collected!: Set<number>;

  private worldGfx!: Phaser.GameObjects.Graphics;
  private entityGfx!: Phaser.GameObjects.Graphics;
  /** Per-tile sprite overlays (foliage/water/ground variants). Empty if no art loaded. */
  private tileSprites: Phaser.GameObjects.Image[] = [];
  /**
   * Subset of tile sprites that are water — animated Sprites playing the
   * water ripple loop. Tracked separately so they can be paused/resumed and
   * cleaned up. Empty if no water art loaded.
   */
  private waterSprites: Phaser.GameObjects.Sprite[] = [];
  /** Per-coconut sprite (parallel to playfield.coconuts). Empty if no art loaded. */
  private coconutSprites: (Phaser.GameObjects.Image | null)[] = [];
  private telegraphGfx!: Phaser.GameObjects.Graphics;
  /**
   * Debug stats line at the bottom of the viewport. Built only in development
   * (Vite dev server), undefined in production builds so it never renders for
   * real players.
   */
  private statsText?: Phaser.GameObjects.Text;
  private hudText!: Phaser.GameObjects.Text;
  private resultOverlay!: Phaser.GameObjects.Container;
  private resultBg!: Phaser.GameObjects.Rectangle;
  private resultTitle!: Phaser.GameObjects.Text;
  private resultBody!: Phaser.GameObjects.Text;
  private resultHelp!: Phaser.GameObjects.Text;
  /**
   * Share button is a DOM element, not a Phaser GameObject. Phaser input
   * routing through nested containers was eating clicks, and the Clipboard
   * API works more reliably from a real DOM user-activation event.
   */
  private shareButtonDom!: HTMLButtonElement;
  /**
   * Retry button on the result overlay. Shown when the run is in a state
   * that allows another attempt (mid-adventure with lives, or any
   * daily/quick mode death). DOM for the same touch-reliability reasons
   * as the other overlay buttons.
   */
  private retryButtonDom!: HTMLButtonElement;
  /**
   * Menu/back button. DOM element for the same reasons as the share button:
   * Phaser's input on top of the full-screen world isn't reliable enough on
   * touch, and Esc-only would lock touch-only users out of the menu.
   */
  private menuButtonDom!: HTMLButtonElement;
  /**
   * PAUSE button. DOM element for touch reliability. Tapping it pauses the
   * run; the MENU button reuses this code path so that exiting mid-run gives
   * the player a confirmation instead of instantly bailing.
   */
  private pauseButtonDom!: HTMLButtonElement;

  /** Pause overlay (Phaser, screen-space). Shown while `paused` is true. */
  private pauseOverlay?: Phaser.GameObjects.Container;
  /** Resume DOM button on the pause overlay. */
  private resumeButtonDom?: HTMLButtonElement;
  /** Menu DOM button on the pause overlay (distinct from the top-right menu button). */
  private pauseMenuButtonDom?: HTMLButtonElement;
  /** Stashed listener so we can detach on scene shutdown. */
  private visibilityHandler?: () => void;

  private cameraTween?: Phaser.Tweens.Tween;
  private dead = false;
  private won = false;
  private deathCause: 'edge' | 'water' | 'spider' = 'edge';
  private runElapsedMs = 0;
  /**
   * Paused state. When true: scroll/spiders/grace/run-timer all freeze,
   * input is ignored, the pause overlay is shown. Set via pause()/resume().
   */
  private paused = false;

  /**
   * Pre-scroll grace period in ms. While > 0, the scroll controller is not
   * ticked — but input + collection are live, so the player can pre-position
   * and pick up nearby coconuts before the chaos starts.
   */
  private graceRemainingMs = 0;
  private graceBanner?: Phaser.GameObjects.Text;
  /**
   * Practice-mode onboarding card. Built once, shown only during practice
   * runs while the player is still learning. Auto-dismisses after a few
   * moves or a timeout. Other modes never see it.
   */
  private practiceHintCard?: Phaser.GameObjects.Container;
  /** Moves remaining before the practice hint auto-hides. */
  private practiceHintMovesLeft = 0;

  /** Lives remaining at the start of the current attempt (adventure only). */
  private livesAtAttemptStart = 1;

  /**
   * For daily mode: was the just-completed run the player's first attempt
   * today? Set in showResult; consumed by the overlay text.
   */
  private dailyWasFirstAttempt = false;

  constructor() {
    super({ key: 'Game' });
  }

  init(cfg: RunConfig): void {
    this.cfg = cfg;
    this.livesAtAttemptStart = cfg.lives;
  }

  create(): void {
    SoundManager.setScene(this);
    // This scene is the active pause target for parent SET_PAUSE commands.
    ParentBridge.registerPausable(this);

    this.worldGfx = this.add.graphics();
    this.entityGfx = this.add.graphics();

    this.bootRun(this.cfg);

    this.telegraphGfx = this.add.graphics().setScrollFactor(0).setDepth(100);

    // GRACE banner ("READY... GO!") - screen-space, hidden when grace==0
    this.graceBanner = this.add
      .text(VIEWPORT_PX_W / 2, VIEWPORT_PX_H / 2 - 20, '', {
        fontFamily: 'monospace',
        fontSize: '52px',
        fontStyle: 'bold',
        color: '#e8c87a',
        stroke: '#000000',
        strokeThickness: 4,
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(150)
      .setVisible(false);

    // Practice hint card — built hidden, shown only when bootRun is called
    // with cfg.mode === 'practice'. Positioned along the bottom of the
    // viewport so it doesn't cover the chimp or the playfield action.
    this.buildPracticeHintCard();
    // bootRun ran before the card existed; show now if we're in practice.
    if (this.cfg.mode === 'practice') this.showPracticeHints();
    this.hudText = this.add
      .text(8, 6, '', {
        fontFamily: 'monospace',
        fontSize: '12px',
        color: '#e8e8e8',
        lineSpacing: 2,
        // Semi-transparent panel behind the text so it stays legible against
        // bright/light backgrounds (grass, water, foliage with sprite art).
        // Phaser's `backgroundColor` paints a tight rect behind the glyph
        // bbox; `setPadding` extends it into a comfortable margin.
        backgroundColor: '#000000a8',
      })
      .setPadding(8, 6, 8, 6)
      .setScrollFactor(0)
      .setDepth(100);

    // Debug stats line: only built in development (Vite dev server). In a
    // production build (`npm run build`), import.meta.env.DEV is false and
    // we skip it entirely, so the field stays undefined and updateHud()
    // checks for it before writing.
    if (import.meta.env.DEV) {
      this.statsText = this.add
        .text(8, VIEWPORT_PX_H - 18, '', {
          fontFamily: 'monospace',
          fontSize: '10px',
          color: '#cfcfcf',
          backgroundColor: '#000000a8',
        })
        .setPadding(6, 3, 6, 3)
        .setScrollFactor(0)
        .setDepth(100);
    }

    this.buildResultOverlay();

    // Hotkeys
    this.input.keyboard?.on('keydown', (ev: KeyboardEvent) => {
      if (ev.key === '`' || ev.key === '~') {
        this.scene.start('DebugMaze');
        return;
      }
      // Esc and P toggle pause (mid-run). On the result overlay or after
      // death/win, Esc returns to menu directly.
      if (ev.key === 'Escape' || ev.key === 'p' || ev.key === 'P') {
        if (this.dead || this.won || (this.resultOverlay && this.resultOverlay.visible)) {
          if (ev.key === 'Escape') this.scene.start('Menu');
        } else if (this.paused) {
          this.resume();
        } else {
          this.pause();
        }
        return;
      }
      // While paused, swallow gameplay hotkeys so R doesn't restart a paused run.
      if (this.paused) return;

      // R / N only do something meaningful in quick play (or daily for retries).
      // In adventure, the result overlay drives flow.
      if (this.cfg.mode !== 'adventure') {
        if (ev.key === 'r' || ev.key === 'R') {
          this.restartSameSeed();
        } else if (ev.key === 'n' || ev.key === 'N') {
          this.restartNewRandom();
        }
      }
      // Space (or Enter) advances on the result overlay
      if (
        this.resultOverlay.visible &&
        (ev.key === ' ' || ev.key === 'Enter')
      ) {
        this.handleResultAdvance();
      }
    });

    // Auto-pause when the page is hidden (tab switch, phone lock, etc.) so
    // the run doesn't burn through a daily attempt while the player isn't
    // looking. Only auto-pauses; never auto-resumes — that's the player's
    // explicit decision.
    const visibilityHandler = (): void => {
      if (document.visibilityState === 'hidden') this.pause();
    };
    document.addEventListener('visibilitychange', visibilityHandler);
    // Stash for shutdown cleanup.
    this.visibilityHandler = visibilityHandler;

    // When the scene shuts down (e.g. Esc → Menu), Phaser destroys every
    // GameObject created via this.add.*. Null our refs so the next create()
    // doesn't try to reuse pointers to destroyed objects (which was a real
    // bug: the chimp from the prior session would silently not appear).
    // Also remove our DOM-overlaid share button so it doesn't linger.
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.chimp = undefined as unknown as Chimp;
      this.spiders = undefined as unknown as SpiderManager;
      this.input_ = undefined as unknown as InputController;
      this.cameraTween = undefined;
      // Tile and coconut sprites are GameObjects that Phaser destroys for us
      // on shutdown, but clear the arrays so a re-entry doesn't see stale refs.
      this.tileSprites = [];
      this.coconutSprites = [];
      this.waterSprites = [];
      this.practiceHintCard = undefined;
      // Cancel any pending race-countdown beeps so they don't fire after the
      // scene goes away (would re-trigger SoundManager from a dead context).
      for (const t of this.raceBeepTimers) t.remove();
      this.raceBeepTimers = [];
      if (this.shareButtonDom && this.shareButtonDom.parentElement) {
        this.shareButtonDom.parentElement.removeChild(this.shareButtonDom);
      }
      this.shareButtonDom = undefined as unknown as HTMLButtonElement;
      if (this.retryButtonDom && this.retryButtonDom.parentElement) {
        this.retryButtonDom.parentElement.removeChild(this.retryButtonDom);
      }
      this.retryButtonDom = undefined as unknown as HTMLButtonElement;
      if (this.menuButtonDom && this.menuButtonDom.parentElement) {
        this.menuButtonDom.parentElement.removeChild(this.menuButtonDom);
      }
      this.menuButtonDom = undefined as unknown as HTMLButtonElement;
      // Pause-related DOM + the document-level visibility listener.
      if (this.pauseButtonDom && this.pauseButtonDom.parentElement) {
        this.pauseButtonDom.parentElement.removeChild(this.pauseButtonDom);
      }
      this.pauseButtonDom = undefined as unknown as HTMLButtonElement;
      if (this.resumeButtonDom && this.resumeButtonDom.parentElement) {
        this.resumeButtonDom.parentElement.removeChild(this.resumeButtonDom);
      }
      this.resumeButtonDom = undefined;
      if (this.pauseMenuButtonDom && this.pauseMenuButtonDom.parentElement) {
        this.pauseMenuButtonDom.parentElement.removeChild(this.pauseMenuButtonDom);
      }
      this.pauseMenuButtonDom = undefined;
      if (this.visibilityHandler) {
        document.removeEventListener('visibilitychange', this.visibilityHandler);
        this.visibilityHandler = undefined;
      }
      // Reset paused flag so a fresh activation of this scene starts unpaused.
      this.paused = false;
      // Stop receiving parent SET_PAUSE commands aimed at this (dead) scene.
      ParentBridge.registerPausable(null);
    });
  }

  // --- Boot / restart ----------------------------------------------------

  private bootRun(cfg: RunConfig): void {
    this.cfg = cfg;
    // livesAtAttemptStart is what the death-result logic compares against to
    // decide "out of lives". It was originally set only in init(), but
    // retries and level advances reach this method via bootRun directly
    // (no scene.start), so without this assignment the value would stay
    // stuck at whatever init() saw on the very first entry — giving stale
    // "lives left" numbers and incorrect end-of-run decisions on retries.
    this.livesAtAttemptStart = cfg.lives;
    this.playfield = generatePlayfield(cfg.seed, {
      waterMultiplier: cfg.waterMultiplier,
    });
    this.dead = false;
    this.won = false;
    this.paused = false;
    this.deathCause = 'edge';
    this.runElapsedMs = 0;
    this.collected = new Set();
    this.score = new Score();
    // Pre-scroll grace before the chaos starts. Long enough for the player to
    // read the scene and pre-position; short enough not to feel like a wait.
    // Practice mode gets a longer grace so the onboarding hint is readable.
    this.graceRemainingMs = cfg.mode === 'practice' ? 2400 : 900;

    // A fresh run is interactive again: PAUSE button visible, pause overlay
    // hidden. (Both may have been toggled by the previous run's end state.)
    if (this.pauseButtonDom) this.pauseButtonDom.style.display = 'block';
    if (this.pauseOverlay) this.pauseOverlay.setVisible(false);
    if (this.resumeButtonDom) this.resumeButtonDom.style.display = 'none';
    if (this.pauseMenuButtonDom) this.pauseMenuButtonDom.style.display = 'none';

    const initialCamX = clamp(
      this.playfield.spawn.x - Math.floor(VIEWPORT_TILES_W / 2),
      0,
      PLAYFIELD_TILES_W - VIEWPORT_TILES_W
    );
    const initialCamY = clamp(
      this.playfield.spawn.y - Math.floor(VIEWPORT_TILES_H / 2),
      0,
      PLAYFIELD_TILES_H - VIEWPORT_TILES_H
    );

    this.scroll = new ScrollController({
      seed: cfg.seed,
      worldW: PLAYFIELD_TILES_W,
      worldH: PLAYFIELD_TILES_H,
      viewW: VIEWPORT_TILES_W,
      viewH: VIEWPORT_TILES_H,
      initialCameraX: initialCamX,
      initialCameraY: initialCamY,
      intervalMul: cfg.scrollIntervalMul,
    });
    // Bias the scroll's target picker toward areas with coconuts. The picker
    // still uses its seeded RNG to choose; this just adds a deterministic
    // weight per tile, so same-input playthroughs stay reproducible.
    this.scroll.setCoconutPositions(this.playfield.coconuts);

    this.drawWorld();
    this.drawEntities();

    const cam = this.cameras.main;
    cam.setBounds(
      0,
      0,
      PLAYFIELD_TILES_W * TILE_SIZE,
      PLAYFIELD_TILES_H * TILE_SIZE
    );
    cam.setScroll(initialCamX * TILE_SIZE, initialCamY * TILE_SIZE);

    if (this.chimp) this.chimp.destroy();
    this.chimp = new Chimp(this, this.playfield.spawn.x, this.playfield.spawn.y);

    if (this.spiders) this.spiders.destroy();
    this.spiders = new SpiderManager(this);
    this.spiders.setTickInterval(SPIDER_BASE_TICK_MS * cfg.spiderTickMul);
    this.spiders.rebuild(this.playfield.spiders);

    // InputController doesn't own GameObjects, but its cached Key references
    // and pointer ref can become stale across scene shutdown/restart. Cheap
    // to recreate; safer than reusing. Touch scheme comes from saved settings
    // (defaults to 'swipe').
    const savedTouchMode = load().settings.touchMode ?? 'swipe';
    this.input_ = new InputController(
      this,
      VIEWPORT_PX_W,
      VIEWPORT_PX_H,
      savedTouchMode
    );

    // Practice mode shows an onboarding hint card; every other mode hides it.
    // We check the card exists because bootRun is called early in create()
    // before buildPracticeHintCard runs the first time.
    if (this.practiceHintCard) {
      if (cfg.mode === 'practice') {
        this.showPracticeHints();
      } else {
        this.practiceHintCard.setVisible(false);
      }
    }

    // Schedule the race-start countdown beeps. Timed so the final (GO) beep
    // fires when the banner flips to "GO!" — 300ms before grace ends.
    this.scheduleRaceCountdown();
  }

  /** Timers driving the race-start countdown beeps. Cancelled on bootRun + shutdown. */
  private raceBeepTimers: Phaser.Time.TimerEvent[] = [];

  /**
   * Schedule the race-start beeps. The countdown is always the same shape
   * across all modes — two ready beeps + a GO beep, 300ms apart — so the
   * rhythm a player learns transfers between modes.
   *
   * In practice mode the grace is longer (2400ms), but instead of stretching
   * the countdown to fill that time, we *shift it to the back* — silence
   * while the player reads the hint card, then the same crisp countdown
   * as everywhere else, with the GO beep landing exactly when the banner
   * flips to "GO!" (300ms before scroll starts).
   *
   * Layout (offsets from grace start):
   *   countdown duration: 600ms (ready + 300 + ready + 300 + GO)
   *   GO beep lands at:   graceTotal - 300
   *   countdown starts:   graceTotal - 900
   *
   *   standard (900ms grace):  beeps at 0, 300, 600 — GO at 600
   *   practice (2400ms grace): beeps at 1500, 1800, 2100 — GO at 2100
   */
  private scheduleRaceCountdown(): void {
    // Cancel any countdown left over from a previous run (retry / next level).
    for (const t of this.raceBeepTimers) t.remove();
    this.raceBeepTimers = [];

    const graceTotal = this.graceRemainingMs;
    const countdownStart = Math.max(0, graceTotal - 900);
    const ready1At = countdownStart;
    const ready2At = countdownStart + 300;
    const goAt = countdownStart + 600;

    const queue = (atMs: number, kind: 'ready' | 'go') => {
      const ev = this.time.delayedCall(atMs, () => {
        if (this.dead || this.won || this.paused) return;
        SoundManager.playRaceBeep(kind);
      });
      this.raceBeepTimers.push(ev);
    };
    queue(ready1At, 'ready');
    queue(ready2At, 'ready');
    queue(goAt, 'go');
  }

  private restartSameSeed(): void {
    if (this.cameraTween) this.cameraTween.stop();
    this.hideResultOverlay();
    this.bootRun(this.cfg);
  }

  private restartNewRandom(): void {
    if (this.cameraTween) this.cameraTween.stop();
    this.hideResultOverlay();
    const newCfg: RunConfig = {
      ...this.cfg,
      seed: Math.random().toString(36).slice(2, 10),
    };
    this.bootRun(newCfg);
  }

  /** Hide overlay panel AND its DOM buttons together. */
  private hideResultOverlay(): void {
    if (this.resultOverlay) this.resultOverlay.setVisible(false);
    if (this.shareButtonDom) this.shareButtonDom.style.display = 'none';
    if (this.retryButtonDom) this.retryButtonDom.style.display = 'none';
  }

  // --- Main loop ---------------------------------------------------------

  override update(_time: number, delta: number): void {
    // While paused, do nothing — frozen frame. The chimp tick is also
    // skipped so the walk animation stays put (otherwise the linger window
    // would expire mid-pause and the chimp would visibly snap to idle).
    if (this.paused) return;

    // Per-frame: drive chimp walk anim (cheap no-op if no anim is in use).
    if (this.chimp) this.chimp.tick(this.time.now);

    // Grace period: input + pickup are live, but the scroll and spiders
    // are paused. Banner counts down in updateGraceBanner.
    const inGrace = this.graceRemainingMs > 0;
    if (inGrace) {
      this.graceRemainingMs -= delta;
      this.updateGraceBanner();
    } else if (this.graceBanner) {
      // Just exited grace — hide the banner.
      this.graceBanner.setVisible(false);
    }

    if (!inGrace) {
      const result = this.scroll.update(delta);
      if (result.scrolled) this.tweenCamera();
    }

    if (!this.dead && !this.won) {
      if (!inGrace) this.runElapsedMs += delta;

      const dir = this.input_.consume(this.time.now);
      if (dir) {
        const moved = this.chimp.tryMove(dir, this.playfield);
        if (moved) {
          // Practice hint auto-dismisses after a few successful moves so it
          // doesn't linger once the player clearly knows how to walk.
          if (this.practiceHintMovesLeft > 0) {
            this.practiceHintMovesLeft--;
            if (this.practiceHintMovesLeft === 0) this.hidePracticeHints();
          }
          if (!this.checkChimpTileHazard()) {
            this.checkCoconutPickup();
          }
        }
      }

      if (!inGrace && !this.dead && this.spiders.update(delta)) {
        if (this.spiders.isAt(this.chimp.tileX, this.chimp.tileY)) {
          this.die('spider');
        }
      }

      if (!inGrace && !this.dead && this.chimpIsOffscreen()) this.die('edge');
    } else if (!inGrace) {
      this.spiders.update(delta);
    }

    this.drawTelegraph();
    this.updateHud();
  }

  // --- Game logic --------------------------------------------------------

  private checkChimpTileHazard(): boolean {
    const x = this.chimp.tileX;
    const y = this.chimp.tileY;
    if (this.playfield.tiles[y * this.playfield.width + x] === Tile.Water) {
      this.die('water');
      return true;
    }
    if (this.spiders.isAt(x, y)) {
      this.die('spider');
      return true;
    }
    return false;
  }

  private checkCoconutPickup(): void {
    const cx = this.chimp.tileX;
    const cy = this.chimp.tileY;
    const coconuts = this.playfield.coconuts;

    for (let i = 0; i < coconuts.length; i++) {
      if (this.collected.has(i)) continue;
      const c = coconuts[i]!;
      if (c.x === cx && c.y === cy) {
        const pts = this.score.collect(c.invisible);
        this.collected.add(i);
        this.spawnFloatingScore(cx, cy, pts, c.invisible);
        // Sound + haptic: invisible coconuts get the brighter cue
        if (c.invisible) {
          SoundManager.playInvisibleCoconut();
          HapticsManager.playInvisibleCoconut();
        } else {
          SoundManager.playCoconut();
          HapticsManager.playCoconut();
        }
        this.drawEntities();
        // Refresh the scroll picker's coconut bias with the still-uncollected
        // set so the camera stops being pulled toward this now-empty tile.
        const remaining = coconuts.filter((_, j) => !this.collected.has(j));
        this.scroll.setCoconutPositions(remaining);
        if (this.score.coconutsCollected === coconuts.length) this.win();
        return;
      }
    }
  }

  private spawnFloatingScore(tileX: number, tileY: number, points: number, invisible: boolean): void {
    const wx = tileX * TILE_SIZE + TILE_SIZE / 2;
    const wy = tileY * TILE_SIZE + TILE_SIZE / 2;
    const text = invisible ? `+${points} ★` : `+${points}`;
    const color = invisible ? '#ffe27a' : '#ffffff';
    const t = this.add
      .text(wx, wy - 4, text, {
        fontFamily: 'monospace',
        fontSize: invisible ? '16px' : '14px',
        color,
        fontStyle: invisible ? 'bold' : 'normal',
      })
      .setOrigin(0.5)
      .setDepth(60);
    this.tweens.add({
      targets: t,
      y: wy - 28,
      alpha: 0,
      duration: 700,
      ease: 'Sine.easeOut',
      onComplete: () => t.destroy(),
    });
  }

  private chimpIsOffscreen(): boolean {
    const camX = this.scroll.cameraTileX;
    const camY = this.scroll.cameraTileY;
    const cx = this.chimp.tileX;
    const cy = this.chimp.tileY;
    return (
      cx < camX + VIEWPORT_DEATH_MARGIN ||
      cx > camX + VIEWPORT_TILES_W - 1 - VIEWPORT_DEATH_MARGIN ||
      cy < camY + VIEWPORT_DEATH_MARGIN ||
      cy > camY + VIEWPORT_TILES_H - 1 - VIEWPORT_DEATH_MARGIN
    );
  }

  private die(cause: 'edge' | 'water' | 'spider'): void {
    if (this.dead || this.won) return;
    this.dead = true;
    this.deathCause = cause;
    this.chimp.kill();
    SoundManager.playSplat();
    HapticsManager.playSplat();
    const summary = this.score.buildSummary(
      this.runElapsedMs,
      this.playfield.coconuts.length,
      false
    );
    this.time.delayedCall(OVERLAY_DELAY_MS, () => this.showResult(summary, false));
  }

  private win(): void {
    if (this.dead || this.won) return;
    this.won = true;
    // Adventure: intermediate levels get the level-clear arpeggio; final
    // level + non-adventure modes get the full hoot.
    if (this.cfg.mode === 'adventure' && this.cfg.level < TOTAL_ADVENTURE_LEVELS) {
      SoundManager.playLevelClear();
      HapticsManager.playLevelClear();
    } else {
      SoundManager.playWin();
      HapticsManager.playWin();
    }
    const summary = this.score.buildSummary(
      this.runElapsedMs,
      this.playfield.coconuts.length,
      true
    );
    this.time.delayedCall(OVERLAY_DELAY_MS, () => this.showResult(summary, true));
  }

  // --- Result handling (mode-aware) -------------------------------------

  private showResult(summary: ScoreSummary, won: boolean): void {
    const cfg = this.cfg;

    // Persistence: record this attempt before drawing the overlay.
    this.persistResult(summary, won);

    if (won) {
      // Win logic differs by mode
      if (cfg.mode === 'adventure') {
        if (cfg.level >= TOTAL_ADVENTURE_LEVELS) {
          // Final level cleared!
          const finalTotal = cfg.carryScore + summary.total;
          this.resultTitle.setText('JUNGLE KING!').setColor('#e8c87a');
          this.resultBody.setText(
            [
              `all 7 levels cleared`,
              `lives left  ${cfg.lives}`,
              ``,
              `level score  ${summary.total}`,
              `carry score  ${cfg.carryScore}`,
              `final total  ${finalTotal}`,
            ].join('\n')
          );
          this.resultHelp.setText('[Space] menu   [Esc] menu');
        } else {
          // Advance to next level
          this.resultTitle.setText(`LEVEL ${cfg.level} CLEAR`).setColor('#7adb6b');
          this.resultBody.setText(
            [
              `level score   ${summary.total}`,
              `running total ${cfg.carryScore + summary.total}`,
              `lives left    ${cfg.lives}`,
              ``,
              `next: level ${cfg.level + 1}`,
            ].join('\n')
          );
          this.resultHelp.setText('[Space] next level   [Esc] menu');
        }
      } else {
        // Daily or Quick
        this.resultTitle.setText('ALL COCONUTS!').setColor('#7adb6b');
        const tag = this.dailyTag();
        const lines = [
          ...(tag ? [tag, ``] : []),
          `coconuts  ${summary.coconutsCollected}/${summary.totalCoconuts}` +
            (summary.invisibleCollected > 0 ? `  (${summary.invisibleCollected} ★)` : ''),
          `time      ${formatTime(summary.elapsedMs)}`,
          ``,
          `score     ${summary.basePoints}`,
          `bonus     +${summary.timeBonus}`,
          `total     ${summary.total}`,
        ];
        this.resultBody.setText(lines.join('\n'));
        this.resultHelp.setText(this.helpForNonAdventure());
      }
    } else {
      // Death logic
      let title = 'SPLAT!';
      let subtitle = 'pushed off the edge';
      let color = '#e84a4a';
      if (this.deathCause === 'water') {
        title = 'DRENCHED!';
        subtitle = "chimps can't swim";
        color = '#5aa8e8';
      } else if (this.deathCause === 'spider') {
        title = 'SQUISHED!';
        subtitle = 'walked into a spider';
        color = '#b048d8';
      }
      this.resultTitle.setText(title).setColor(color);

      if (cfg.mode === 'adventure') {
        const livesAfter = this.livesAtAttemptStart - 1;
        if (livesAfter <= 0) {
          this.resultBody.setText(
            [
              subtitle,
              ``,
              `level reached  ${cfg.level}/${TOTAL_ADVENTURE_LEVELS}`,
              `coconuts       ${summary.coconutsCollected}/${summary.totalCoconuts}` +
                (summary.invisibleCollected > 0 ? `  (${summary.invisibleCollected} ★)` : ''),
              `final score    ${cfg.carryScore + summary.basePoints}`,
              ``,
              `out of lives`,
            ].join('\n')
          );
          this.resultHelp.setText('[Space] menu   [Esc] menu');
        } else {
          this.resultBody.setText(
            [
              subtitle,
              ``,
              `level         ${cfg.level}/${TOTAL_ADVENTURE_LEVELS}`,
              `coconuts      ${summary.coconutsCollected}/${summary.totalCoconuts}` +
                (summary.invisibleCollected > 0 ? `  (${summary.invisibleCollected} ★)` : ''),
              ``,
              `lives left    ${livesAfter}`,
            ].join('\n')
          );
          this.resultHelp.setText('[Space] retry level   [Esc] menu');
        }
      } else {
        const tag = this.dailyTag();
        this.resultBody.setText(
          [
            subtitle,
            ...(tag ? [``, tag] : []),
            ``,
            `coconuts  ${summary.coconutsCollected}/${summary.totalCoconuts}` +
              (summary.invisibleCollected > 0 ? `  (${summary.invisibleCollected} ★)` : ''),
            `time      ${formatTime(summary.elapsedMs)}`,
            `score     ${summary.total}`,
          ].join('\n')
        );
        this.resultHelp.setText(this.helpForNonAdventure());
      }
    }

    this.updateOverlayButtonVisibility(won);
    // The PAUSE button is meaningless once the run is over — hide it so the
    // result overlay isn't cluttered with a dead control. MENU stays useful.
    if (this.pauseButtonDom) this.pauseButtonDom.style.display = 'none';
    // Practice hint shouldn't peek through under the result overlay.
    if (this.practiceHintCard) this.practiceHintCard.setVisible(false);
    this.resultOverlay.setVisible(true);
  }

  /**
   * Decide which overlay buttons (RETRY, SHARE) appear after a run ends.
   *
   * - RETRY shows whenever pressing Space would lead to another playable run
   *   in the same scene: any daily/quick outcome, mid-adventure death with
   *   lives remaining, or an adventure win that's not the final level.
   * - SHARE shows only on a daily first-attempt run end.
   * - When both show, they sit side by side; otherwise the visible one
   *   centers under the result text.
   */
  private updateOverlayButtonVisibility(won: boolean): void {
    const cfg = this.cfg;
    let showRetry = false;
    let retryLabel = '↻ RETRY';

    if (cfg.mode === 'adventure') {
      if (won && cfg.level < TOTAL_ADVENTURE_LEVELS) {
        showRetry = true;
        retryLabel = '▶ NEXT LEVEL';
      } else if (!won && this.livesAtAttemptStart - 1 > 0) {
        showRetry = true;
        retryLabel = '↻ RETRY LEVEL';
      }
    } else {
      // Daily / quick — retry always available after a run.
      showRetry = true;
      retryLabel = won ? '↻ AGAIN' : '↻ RETRY';
    }

    const showShare = cfg.mode === 'daily' && this.dailyWasFirstAttempt;

    if (showShare) {
      this.resetShareButtonDom();
      // Side-by-side positioning happens in positionRetryDomButton(false).
      this.shareButtonDom.style.display = 'block';
    } else {
      this.shareButtonDom.style.display = 'none';
    }

    if (showRetry) {
      this.retryButtonDom.textContent = retryLabel;
      this.retryButtonDom.style.display = 'block';
      this.positionRetryDomButton(!showShare);
    } else {
      this.retryButtonDom.style.display = 'none';
      // If share is showing alone, re-center it (positionRetryDomButton
      // nudges it sideways when both are visible).
      if (showShare) this.positionShareDomButton();
    }
  }

  private helpForNonAdventure(): string {
    if (this.cfg.mode === 'daily') {
      return '[R] retry today   [Esc] menu';
    }
    return '[R] retry seed   [N] new seed   [Esc] menu';
  }

  /**
   * Short label for daily mode that distinguishes first attempt (counts toward
   * streak & share) from replays. Returns null for non-daily modes.
   */
  private dailyTag(): string | null {
    if (this.cfg.mode !== 'daily') return null;
    return this.dailyWasFirstAttempt ? 'first attempt' : 'replay (not counted)';
  }

  /**
   * Record the current run's outcome to localStorage.
   *
   *   - Daily: every attempt updates the daily entry; the first attempt that
   *     day is the one that counts for streak and is flagged in the overlay.
   *   - Adventure: only the END of a run is recorded (final level cleared OR
   *     run-ending death). Per-level intermediate states are not stored.
   *   - Quick: nothing to persist.
   */
  private persistResult(summary: ScoreSummary, won: boolean): void {
    const cfg = this.cfg;
    let state: SaveState = load();

    if (cfg.mode === 'daily') {
      const r = recordDailyAttempt(
        state,
        cfg.seed,
        summary.total,
        won
      );
      state = r.state;
      this.dailyWasFirstAttempt = r.isFirstAttempt;
      commit(state);
      return;
    }

    if (cfg.mode === 'adventure') {
      // Determine whether the *full* adventure run has ended.
      let runEnded = false;
      let finalTotal = 0;
      let highestLevel = cfg.level;

      if (won && cfg.level >= TOTAL_ADVENTURE_LEVELS) {
        runEnded = true;
        finalTotal = cfg.carryScore + summary.total;
        highestLevel = cfg.level;
      } else if (!won) {
        const livesAfter = this.livesAtAttemptStart - 1;
        if (livesAfter <= 0) {
          runEnded = true;
          // Use base points only — no time bonus on death.
          finalTotal = cfg.carryScore + summary.basePoints;
          highestLevel = cfg.level;
        }
      }

      if (runEnded) {
        state = recordAdventureRun(state, finalTotal, highestLevel);
        commit(state);
      }
      return;
    }

    // Quick mode: nothing to save.
  }

  /** Called when the player hits Space/Enter on a visible result overlay. */
  private handleResultAdvance(): void {
    const cfg = this.cfg;

    // Adventure win: advance to next level (or back to menu after final)
    if (this.won && cfg.mode === 'adventure') {
      if (cfg.level >= TOTAL_ADVENTURE_LEVELS) {
        this.scene.start('Menu');
        return;
      }
      const nextLevel = cfg.level + 1;
      const lc = levelConfig(nextLevel);
      const nextCfg: RunConfig = {
        ...cfg,
        seed: `${cfg.adventureRunId ?? 'adv-unknown'}::L${nextLevel}`,
        level: nextLevel,
        scrollIntervalMul: lc.scrollIntervalMul,
        spiderTickMul: lc.spiderTickMul,
        waterMultiplier: lc.waterMultiplier,
        carryScore: cfg.carryScore + this.score.basePoints + this.computeTimeBonusForSummary(),
      };
      this.hideResultOverlay();
      this.bootRun(nextCfg);
      return;
    }

    // Adventure death with lives remaining: retry same level
    if (this.dead && cfg.mode === 'adventure') {
      const livesAfter = this.livesAtAttemptStart - 1;
      if (livesAfter <= 0) {
        this.scene.start('Menu');
        return;
      }
      const retryCfg: RunConfig = { ...cfg, lives: livesAfter };
      this.hideResultOverlay();
      this.bootRun(retryCfg);
      return;
    }

    // Daily / quick / practice: retry the current run. For practice this is
    // the same seed (PRACTICE_SEED), so the player can keep relearning the
    // same playfield. Daily and quick get the same-seed retry (matches the
    // existing R-hotkey behavior).
    if (cfg.mode === 'daily' || cfg.mode === 'quick' || cfg.mode === 'practice') {
      this.restartSameSeed();
      return;
    }
  }

  /** Helper to compute the time bonus on win so we can carry it forward. */
  private computeTimeBonusForSummary(): number {
    return Score.computeTimeBonus(this.runElapsedMs);
  }

  /**
   * Drive the grace-period banner. Three phases:
   *   - >300ms remaining: "READY"
   *   - <=300ms remaining: "GO!"
   * The banner pops in larger and shrinks slightly for energy.
   */
  private updateGraceBanner(): void {
    if (!this.graceBanner) return;
    const rem = this.graceRemainingMs;
    if (rem > 300) {
      this.graceBanner.setText('READY').setColor('#e8c87a').setVisible(true);
    } else {
      this.graceBanner.setText('GO!').setColor('#7adb6b').setVisible(true);
    }
  }

  // --- Rendering --------------------------------------------------------

  /**
   * Draw the static playfield terrain.
   *
   * We render every tile via Graphics first (cheap fallback that always works),
   * then if sprite assets are loaded for a tile type, we draw the sprite on
   * top to mask the placeholder. This way partial asset drops (just `water.png`
   * and `foliage.png`, for example) still look right against the placeholder
   * ground.
   *
   * 24x32 = 768 tile sprites would normally be a lot of GameObjects, but they
   * never move or update — Phaser batches static images very efficiently when
   * the camera is the only moving thing.
   */
  private drawWorld(): void {
    const pf = this.playfield;

    // 1) Graphics fallback layer — always renders, gives us guaranteed tile color.
    this.worldGfx.clear();
    for (let y = 0; y < pf.height; y++) {
      for (let x = 0; x < pf.width; x++) {
        const t = pf.tiles[y * pf.width + x]!;
        const px = x * TILE_SIZE;
        const py = y * TILE_SIZE;
        let base: number;
        if (t === Tile.Foliage) base = C.foliage;
        else if (t === Tile.Water) base = C.water;
        else base = (x + y) % 2 === 0 ? C.ground : C.groundAlt;
        this.worldGfx.fillStyle(base, 1);
        this.worldGfx.fillRect(px, py, TILE_SIZE, TILE_SIZE);
        if (!AssetManager.has('foliage') && t === Tile.Foliage) {
          this.worldGfx.fillStyle(C.foliageEdge, 0.5);
          this.worldGfx.fillRect(px + 4, py + 4, TILE_SIZE - 8, 3);
          this.worldGfx.fillRect(px + 4, py + TILE_SIZE - 7, TILE_SIZE - 8, 3);
        } else if (!AssetManager.has('water') && t === Tile.Water) {
          this.worldGfx.fillStyle(C.waterEdge, 0.7);
          this.worldGfx.fillRect(px + 6, py + TILE_SIZE / 2 - 1, TILE_SIZE - 12, 2);
          this.worldGfx.fillRect(px + 10, py + TILE_SIZE / 2 + 4, TILE_SIZE - 20, 2);
        }
      }
    }

    // 2) Sprite layer — overlay images where assets are available.
    // Tear down any leftover tile sprites from a previous run.
    for (const img of this.tileSprites) img.destroy();
    this.tileSprites = [];
    this.waterSprites = [];

    const hasFoliage = AssetManager.has('foliage');
    const hasWater = AssetManager.has('water');
    const hasGround = AssetManager.has('ground');
    const hasGroundAlt = AssetManager.has('ground_alt');

    if (!hasFoliage && !hasWater && !hasGround && !hasGroundAlt) {
      return; // pure-placeholder rendering, no sprites needed
    }

    for (let y = 0; y < pf.height; y++) {
      for (let x = 0; x < pf.width; x++) {
        const t = pf.tiles[y * pf.width + x]!;
        let key: string | null = null;
        if (t === Tile.Foliage && hasFoliage) key = 'foliage';
        else if (t === Tile.Water && hasWater) key = 'water';
        else if (t === Tile.Ground) {
          const alt = (x + y) % 2 === 0;
          if (alt && hasGround) key = 'ground';
          else if (!alt && hasGroundAlt) key = 'ground_alt';
          else if (hasGround) key = 'ground';
        }
        if (!key) continue;

        const cx = x * TILE_SIZE + TILE_SIZE / 2;
        const cy = y * TILE_SIZE + TILE_SIZE / 2;

        if (t === Tile.Water) {
          // Water is an animated Sprite so it can play its looping ripple.
          const spr = this.add.sprite(cx, cy, key);
          const frameH = spr.frame.height;
          if (frameH !== TILE_SIZE) spr.setScale(TILE_SIZE / frameH);
          spr.setDepth(1);
          const animKey = AssetManager.walkAnimKey('water');
          if (animKey) {
            spr.play(animKey);
            // Desync tiles so they don't ripple in perfect lockstep: jump to a
            // random frame and progress within the animation.
            const frames = AssetManager.frameCount('water');
            if (frames > 1) {
              spr.anims.setProgress(Phaser.Math.Between(0, frames - 1) / frames);
            }
          }
          this.tileSprites.push(spr);
          this.waterSprites.push(spr);
          continue;
        }

        const img = this.add.image(cx, cy, key);
        // Auto-scale if source image isn't TILE_SIZE.
        const tex = img.texture.getSourceImage();
        const longest = Math.max(tex.width, tex.height);
        if (longest !== TILE_SIZE) img.setScale(TILE_SIZE / longest);
        img.setDepth(1); // above worldGfx (depth 0), below entityGfx (auto)
        this.tileSprites.push(img);
      }
    }
  }

  private drawEntities(): void {
    const pf = this.playfield;
    this.entityGfx.clear();

    for (const sp of pf.spiders) {
      const sx1 = sp.start.x * TILE_SIZE + TILE_SIZE / 2;
      const sy1 = sp.start.y * TILE_SIZE + TILE_SIZE / 2;
      const endX = sp.axis === 'horizontal' ? sp.start.x + sp.length - 1 : sp.start.x;
      const endY = sp.axis === 'vertical' ? sp.start.y + sp.length - 1 : sp.start.y;
      const sx2 = endX * TILE_SIZE + TILE_SIZE / 2;
      const sy2 = endY * TILE_SIZE + TILE_SIZE / 2;
      this.entityGfx.lineStyle(3, C.spiderPatrol, 0.55);
      this.entityGfx.lineBetween(sx1, sy1, sx2, sy2);
    }

    // Tear down existing coconut sprites — drawEntities is called when a
    // coconut is collected, and we re-build only those still uncollected.
    for (const s of this.coconutSprites) if (s) s.destroy();
    this.coconutSprites = new Array(pf.coconuts.length).fill(null);

    const useSprite = AssetManager.has('coconut');

    for (let i = 0; i < pf.coconuts.length; i++) {
      if (this.collected.has(i)) continue;
      const c = pf.coconuts[i]!;
      const cx = c.x * TILE_SIZE + TILE_SIZE / 2;
      const cy = c.y * TILE_SIZE + TILE_SIZE / 2;
      if (useSprite) {
        const img = this.add.image(cx, cy, 'coconut');
        const tex = img.texture.getSourceImage();
        const longest = Math.max(tex.width, tex.height);
        if (longest !== TILE_SIZE) img.setScale(TILE_SIZE / longest);
        // Invisible coconuts: render at very low alpha so player needs to learn
        // the playfield, but a faint shimmer is detectable. Visible ones full.
        img.setAlpha(c.invisible ? 0.18 : 1);
        img.setDepth(30);
        this.coconutSprites[i] = img;
      } else {
        // Placeholder graphics path
        if (c.invisible) {
          this.entityGfx.lineStyle(1, C.coconutInvisible, 0.35);
          this.entityGfx.strokeCircle(cx, cy, TILE_SIZE * 0.22);
        } else {
          this.entityGfx.fillStyle(C.coconut, 1);
          this.entityGfx.fillCircle(cx, cy, TILE_SIZE * 0.24);
          this.entityGfx.fillStyle(C.coconutShine, 0.7);
          this.entityGfx.fillCircle(cx - 3, cy - 4, TILE_SIZE * 0.08);
        }
      }
    }
  }

  private drawTelegraph(): void {
    this.telegraphGfx.clear();
    const t = this.scroll.telegraph;
    if (!t) return;
    const alpha = Math.min(1, t.progress / 0.4);
    const dir = t.dir;
    const w = VIEWPORT_PX_W;
    const h = VIEWPORT_PX_H;
    const margin = 8;
    const size = 22;
    let cx = 0, cy = 0;
    let p1x = 0, p1y = 0, p2x = 0, p2y = 0, p3x = 0, p3y = 0;
    if (dir === 'right') {
      cx = w - margin - size; cy = h / 2;
      p1x = cx + size; p1y = cy;
      p2x = cx; p2y = cy - size * 0.7;
      p3x = cx; p3y = cy + size * 0.7;
    } else if (dir === 'left') {
      cx = margin + size; cy = h / 2;
      p1x = cx - size; p1y = cy;
      p2x = cx; p2y = cy - size * 0.7;
      p3x = cx; p3y = cy + size * 0.7;
    } else if (dir === 'up') {
      cx = w / 2; cy = margin + size;
      p1x = cx; p1y = cy - size;
      p2x = cx - size * 0.7; p2y = cy;
      p3x = cx + size * 0.7; p3y = cy;
    } else {
      cx = w / 2; cy = h - margin - size;
      p1x = cx; p1y = cy + size;
      p2x = cx - size * 0.7; p2y = cy;
      p3x = cx + size * 0.7; p3y = cy;
    }
    this.telegraphGfx.fillStyle(C.telegraphGlow, alpha * 0.25);
    this.telegraphGfx.fillTriangle(
      p1x + sign(p1x - cx) * 4, p1y + sign(p1y - cy) * 4,
      p2x + sign(p2x - cx) * 4, p2y + sign(p2y - cy) * 4,
      p3x + sign(p3x - cx) * 4, p3y + sign(p3y - cy) * 4
    );
    this.telegraphGfx.fillStyle(C.telegraph, alpha);
    this.telegraphGfx.fillTriangle(p1x, p1y, p2x, p2y, p3x, p3y);
  }

  private updateHud(): void {
    const total = this.playfield.coconuts.length;
    const collected = this.score.coconutsCollected;
    const liveScore = this.cfg.carryScore + this.score.basePoints;
    const timeStr = formatTime(this.runElapsedMs);

    const lines: string[] = [];
    if (this.cfg.mode === 'adventure') {
      lines.push(`L${this.cfg.level}/${TOTAL_ADVENTURE_LEVELS}   ♥ ${this.cfg.lives}`);
    } else if (this.cfg.mode === 'daily') {
      lines.push(`DAILY  ${this.cfg.seed}`);
    } else if (this.cfg.mode === 'practice') {
      lines.push(`PRACTICE`);
    } else {
      lines.push(`QUICK  ${this.cfg.seed}`);
    }
    lines.push(`SCORE  ${liveScore.toString().padStart(5, '0')}`);
    lines.push(`🥥  ${collected}/${total}`);
    lines.push(`TIME   ${timeStr}`);
    this.hudText.setText(lines.join('\n'));

    // statsText is only built in dev (see create()). Gate the whole update
    // on import.meta.env.DEV so the string literal itself is tree-shaken out
    // of the production bundle, not just the runtime call.
    if (import.meta.env.DEV && this.statsText) {
      this.statsText.setText(
        `int:${Math.round(this.scroll.currentIntervalMs)}ms  scrolls:${this.scroll.scrolls}  ` +
          `[Esc] menu  [\`] debug`
      );
    }
  }

  private buildResultOverlay(): void {
    const w = VIEWPORT_PX_W;
    const h = VIEWPORT_PX_H;
    this.resultBg = this.add.rectangle(w / 2, h / 2, w, h, 0x000000, 0.6);
    this.resultTitle = this.add
      .text(w / 2, h / 2 - 100, '', {
        fontFamily: 'monospace',
        fontSize: '40px',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    this.resultBody = this.add
      .text(w / 2, h / 2 - 10, '', {
        fontFamily: 'monospace',
        fontSize: '13px',
        color: '#dddddd',
        align: 'center',
        lineSpacing: 4,
      })
      .setOrigin(0.5);

    this.resultHelp = this.add
      .text(w / 2, h / 2 + 145, '', {
        fontFamily: 'monospace',
        fontSize: '11px',
        color: '#888',
      })
      .setOrigin(0.5);

    this.resultOverlay = this.add.container(0, 0, [
      this.resultBg,
      this.resultTitle,
      this.resultBody,
      this.resultHelp,
    ]);
    this.resultOverlay.setScrollFactor(0).setDepth(200).setVisible(false);

    // Build the share button as a real DOM element overlaid on the canvas.
    // Why DOM, not a Phaser GameObject:
    //   1. Phaser's input through nested containers proved unreliable here —
    //      the overlay bg kept eating clicks.
    //   2. The Clipboard API requires a "real" user-activation event in many
    //      browsers. A DOM click handler satisfies that requirement; a
    //      Phaser-synthesized pointerdown callback does not on some platforms.
    // We size and position it absolutely over the canvas, then update its
    // CSS transform if the canvas scales (Phaser FIT scale).
    this.shareButtonDom = this.buildShareDomButton();
    // Retry button on the result overlay (touch users can't be expected to
    // know about Space; need a visible affordance to try again).
    this.retryButtonDom = this.buildRetryDomButton();

    // Always-visible MENU button. Tapping it pauses the run and opens the
    // pause overlay — gives a confirmation step before leaving a daily.
    this.menuButtonDom = this.buildMenuDomButton();
    // Always-visible PAUSE button. Different code path than MENU but visually
    // a peer in the top-right corner.
    this.pauseButtonDom = this.buildPauseDomButton();

    // Build the pause overlay (Phaser scene-space) and its two DOM buttons.
    this.buildPauseOverlay();

    this.resultBg.setInteractive();
    this.resultBg.on('pointerdown', () => {
      if (this.resultOverlay.visible) this.handleResultAdvance();
    });

    // Keep DOM buttons positioned correctly when the canvas resizes
    // (orientation change, window resize).
    this.scale.on('resize', () => {
      this.positionShareDomButton();
      this.positionMenuDomButton();
      this.positionPauseDomButton();
      this.positionPauseOverlayButtons();
      // Retry repositions itself based on whether share is also visible.
      if (this.retryButtonDom && this.retryButtonDom.style.display !== 'none') {
        const shareVisible =
          this.shareButtonDom && this.shareButtonDom.style.display !== 'none';
        this.positionRetryDomButton(!shareVisible);
      }
    });
  }

  /**
   * Build the practice-mode onboarding hint card. A small panel anchored to
   * the bottom of the viewport listing the core rules:
   *   • how to move
   *   • that edges, water and spiders kill
   *   • that ~15% of coconuts are hidden
   * Hidden by default; showPracticeHints() displays it; hidePracticeHints()
   * dismisses it (called on first move + after a few moves, whichever comes
   * first).
   */
  private buildPracticeHintCard(): void {
    const w = VIEWPORT_PX_W;
    const h = VIEWPORT_PX_H;
    const cardH = 130;
    const cardY = h - cardH / 2 - 8;
    const bg = this.add.rectangle(w / 2, cardY, w - 16, cardH, 0x0d0d10, 0.92);
    bg.setStrokeStyle(2, 0xe8c87a, 0.6);
    const title = this.add
      .text(w / 2, cardY - cardH / 2 + 14, 'PRACTICE — the rules', {
        fontFamily: 'monospace',
        fontSize: '12px',
        fontStyle: 'bold',
        color: '#e8c87a',
      })
      .setOrigin(0.5, 0);
    const body = this.add
      .text(
        w / 2,
        cardY - 6,
        [
          'arrows / WASD / swipe to move',
          'screen scrolls on its own — yellow arrow warns',
          'edges, water, spiders all kill',
          '🥥 visible, ⭐ hidden (more points)',
        ].join('\n'),
        {
          fontFamily: 'monospace',
          fontSize: '11px',
          color: '#dddddd',
          align: 'center',
          lineSpacing: 3,
        }
      )
      .setOrigin(0.5, 0.5);
    const dismissHint = this.add
      .text(w / 2, cardY + cardH / 2 - 14, 'move to dismiss', {
        fontFamily: 'monospace',
        fontSize: '9px',
        color: '#888',
        fontStyle: 'italic',
      })
      .setOrigin(0.5, 1);

    this.practiceHintCard = this.add.container(0, 0, [bg, title, body, dismissHint]);
    this.practiceHintCard.setScrollFactor(0).setDepth(140).setVisible(false);
  }

  /** Show the practice hint card and set the auto-dismiss counter. */
  private showPracticeHints(): void {
    if (!this.practiceHintCard) return;
    this.practiceHintCard.setVisible(true);
    this.practiceHintCard.setAlpha(1);
    // Auto-dismiss after a few moves. Players who flick or hold movement
    // dismiss it almost immediately; idle players see it through the grace
    // period and into the first scroll.
    this.practiceHintMovesLeft = 3;
  }

  /** Hide the practice hint card (called on first input + after a move count). */
  private hidePracticeHints(): void {
    if (!this.practiceHintCard || !this.practiceHintCard.visible) return;
    // Quick fade so it doesn't snap away.
    this.tweens.add({
      targets: this.practiceHintCard,
      alpha: 0,
      duration: 250,
      ease: 'Sine.easeOut',
      onComplete: () => {
        if (this.practiceHintCard) this.practiceHintCard.setVisible(false);
      },
    });
    this.practiceHintMovesLeft = 0;
  }

  /**
   * Build the DOM share button, append it to the game parent, and return the
   * element. Starts hidden — updateOverlayButtonVisibility() controls display.
   */
  private buildShareDomButton(): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = 'SHARE';
    btn.setAttribute('aria-label', 'Share daily result');
    btn.style.position = 'absolute';
    btn.style.zIndex = '10';
    btn.style.padding = '0';
    btn.style.width = '160px';
    btn.style.height = '36px';
    btn.style.background = '#2a6ba6';
    btn.style.color = '#ffffff';
    btn.style.border = '2px solid rgba(255,255,255,0.25)';
    btn.style.borderRadius = '4px';
    btn.style.fontFamily = 'monospace';
    btn.style.fontSize = '15px';
    btn.style.fontWeight = 'bold';
    btn.style.cursor = 'pointer';
    btn.style.display = 'none';
    btn.style.touchAction = 'manipulation'; // suppress 300ms tap delay
    btn.style.pointerEvents = 'auto';
    btn.addEventListener('mouseenter', () => {
      btn.style.borderColor = 'rgba(255,255,255,0.6)';
    });
    btn.addEventListener('mouseleave', () => {
      btn.style.borderColor = 'rgba(255,255,255,0.25)';
    });
    btn.addEventListener('click', (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      this.onShareClick();
    });

    // Append to the same parent the canvas is in so it sits over the game.
    const parent =
      (this.game.canvas.parentElement as HTMLElement | null) ??
      document.body;
    // Make sure parent supports absolute positioning of children.
    if (parent.style.position === '' || parent.style.position === 'static') {
      parent.style.position = 'relative';
    }
    parent.appendChild(btn);
    return btn;
  }

  /**
   * Place the DOM share button over the canvas at the same logical position
   * (w/2, h/2 + 95) as the rest of the overlay text. Uses the Phaser scale
   * manager's display rect so it stays aligned through FIT scaling.
   */
  private positionShareDomButton(): void {
    if (!this.shareButtonDom) return;
    const canvas = this.game.canvas;
    const canvasRect = canvas.getBoundingClientRect();
    const parent = this.shareButtonDom.offsetParent as HTMLElement | null;
    const parentRect = parent ? parent.getBoundingClientRect() : { left: 0, top: 0 };

    // Phaser's display size accounts for FIT scaling.
    const displayScaleX = canvasRect.width / VIEWPORT_PX_W;
    const displayScaleY = canvasRect.height / VIEWPORT_PX_H;

    const logicalCx = VIEWPORT_PX_W / 2;
    const logicalCy = VIEWPORT_PX_H / 2 + 95;
    const screenX = canvasRect.left + logicalCx * displayScaleX;
    const screenY = canvasRect.top + logicalCy * displayScaleY;

    // Translate to the offset-parent coordinate space.
    const leftPx = screenX - parentRect.left;
    const topPx = screenY - parentRect.top;

    // Match button visual size to the scaled canvas so it looks proportional.
    const btnW = 160 * displayScaleX;
    const btnH = 36 * displayScaleY;
    this.shareButtonDom.style.width = `${btnW}px`;
    this.shareButtonDom.style.height = `${btnH}px`;
    this.shareButtonDom.style.fontSize = `${15 * displayScaleY}px`;
    this.shareButtonDom.style.left = `${leftPx - btnW / 2}px`;
    this.shareButtonDom.style.top = `${topPx - btnH / 2}px`;
  }

  /**
   * Build the result-overlay RETRY button. Same DOM-overlay pattern as the
   * share button. Hidden by default; showResult decides whether to display it
   * based on mode + remaining lives.
   */
  private buildRetryDomButton(): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = '↻ RETRY';
    btn.setAttribute('aria-label', 'Retry');
    btn.style.position = 'absolute';
    btn.style.zIndex = '10';
    btn.style.padding = '0';
    btn.style.background = '#2a8a4a';
    btn.style.color = '#ffffff';
    btn.style.border = '2px solid rgba(255,255,255,0.25)';
    btn.style.borderRadius = '4px';
    btn.style.fontFamily = 'monospace';
    btn.style.fontSize = '15px';
    btn.style.fontWeight = 'bold';
    btn.style.cursor = 'pointer';
    btn.style.display = 'none';
    btn.style.touchAction = 'manipulation';
    btn.style.pointerEvents = 'auto';
    btn.addEventListener('mouseenter', () => {
      btn.style.borderColor = 'rgba(255,255,255,0.6)';
    });
    btn.addEventListener('mouseleave', () => {
      btn.style.borderColor = 'rgba(255,255,255,0.25)';
    });
    btn.addEventListener('click', (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      this.handleResultAdvance();
    });
    const parent =
      (this.game.canvas.parentElement as HTMLElement | null) ??
      document.body;
    if (parent.style.position === '' || parent.style.position === 'static') {
      parent.style.position = 'relative';
    }
    parent.appendChild(btn);
    return btn;
  }

  /**
   * Place the RETRY button on the result overlay. Sits to the LEFT of where
   * SHARE would be (when share is visible they appear side by side). When
   * only RETRY is visible, it centers under the overlay text.
   */
  private positionRetryDomButton(centerUnderText: boolean): void {
    if (!this.retryButtonDom) return;
    const canvas = this.game.canvas;
    const canvasRect = canvas.getBoundingClientRect();
    const parent = this.retryButtonDom.offsetParent as HTMLElement | null;
    const parentRect = parent ? parent.getBoundingClientRect() : { left: 0, top: 0 };

    const displayScaleX = canvasRect.width / VIEWPORT_PX_W;
    const displayScaleY = canvasRect.height / VIEWPORT_PX_H;

    const btnW = 160 * displayScaleX;
    const btnH = 36 * displayScaleY;
    this.retryButtonDom.style.width = `${btnW}px`;
    this.retryButtonDom.style.height = `${btnH}px`;
    this.retryButtonDom.style.fontSize = `${15 * displayScaleY}px`;

    // Same Y as share. X depends on whether share is also showing:
    // centered alone, or to the left of share (with a gap).
    const logicalCy = VIEWPORT_PX_H / 2 + 95;
    const screenY = canvasRect.top + logicalCy * displayScaleY;
    const topPx = screenY - parentRect.top - btnH / 2;
    this.retryButtonDom.style.top = `${topPx}px`;

    const logicalCx = VIEWPORT_PX_W / 2;
    const screenCx = canvasRect.left + logicalCx * displayScaleX;
    if (centerUnderText) {
      this.retryButtonDom.style.left = `${screenCx - parentRect.left - btnW / 2}px`;
    } else {
      // Side by side with share: RETRY on the left, gap of 12px, SHARE on right.
      const gap = 12 * displayScaleX;
      this.retryButtonDom.style.left = `${screenCx - parentRect.left - btnW - gap / 2}px`;
      // And nudge share right of center.
      if (this.shareButtonDom) {
        this.shareButtonDom.style.left = `${screenCx - parentRect.left + gap / 2}px`;
      }
    }
  }

  /**
   * Build the always-on MENU button pinned to the top-left of the canvas.
   * Gives touch-only users a way to return to the menu without relying on Esc.
   */
  private buildMenuDomButton(): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = '☰ MENU';
    btn.setAttribute('aria-label', 'Return to menu');
    btn.style.position = 'absolute';
    btn.style.zIndex = '10';
    btn.style.padding = '0';
    btn.style.background = 'rgba(0,0,0,0.55)';
    btn.style.color = '#ffffff';
    btn.style.border = '1px solid rgba(255,255,255,0.25)';
    btn.style.borderRadius = '4px';
    btn.style.fontFamily = 'monospace';
    btn.style.fontWeight = 'bold';
    btn.style.cursor = 'pointer';
    btn.style.touchAction = 'manipulation';
    btn.style.pointerEvents = 'auto';
    btn.addEventListener('mouseenter', () => {
      btn.style.borderColor = 'rgba(255,255,255,0.6)';
    });
    btn.addEventListener('mouseleave', () => {
      btn.style.borderColor = 'rgba(255,255,255,0.25)';
    });
    btn.addEventListener('click', (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      // If the run is over (result overlay showing) go straight to the menu.
      // If already paused, the player is tapping MENU on the corner again —
      // treat that as "yes, leave" and go to the menu. Otherwise pause first
      // so there's a confirmation step before leaving a live run.
      if (this.dead || this.won || (this.resultOverlay && this.resultOverlay.visible)) {
        this.goToMenu();
      } else if (this.paused) {
        this.goToMenu();
      } else {
        this.pause();
      }
    });

    const parent =
      (this.game.canvas.parentElement as HTMLElement | null) ??
      document.body;
    if (parent.style.position === '' || parent.style.position === 'static') {
      parent.style.position = 'relative';
    }
    parent.appendChild(btn);
    this.positionTopRightButton(btn, 0);
    return btn;
  }

  /**
   * Pin the MENU button to the top-right of the canvas. The second arg lets
   * us stack peer buttons (PAUSE) to its left.
   */
  private positionMenuDomButton(): void {
    if (!this.menuButtonDom) return;
    // MENU sits at the rightmost position (index 0).
    this.positionTopRightButton(this.menuButtonDom, 0);
  }

  /**
   * Stack a corner button at the top-right of the canvas, offset by `index`
   * button widths from the right edge (index 0 = rightmost).
   */
  private positionTopRightButton(btn: HTMLButtonElement, index: number): void {
    const canvas = this.game.canvas;
    const canvasRect = canvas.getBoundingClientRect();
    const parent = btn.offsetParent as HTMLElement | null;
    const parentRect = parent ? parent.getBoundingClientRect() : { left: 0, top: 0 };

    const displayScaleX = canvasRect.width / VIEWPORT_PX_W;
    const displayScaleY = canvasRect.height / VIEWPORT_PX_H;

    const btnW = 80 * displayScaleX;
    const btnH = 26 * displayScaleY;
    const insetX = 8 * displayScaleX;
    const insetY = 6 * displayScaleY;
    const gap = 6 * displayScaleX;

    btn.style.width = `${btnW}px`;
    btn.style.height = `${btnH}px`;
    btn.style.fontSize = `${11 * displayScaleY}px`;

    const screenRight = canvasRect.left + canvasRect.width;
    const screenTop = canvasRect.top;
    const leftPx =
      screenRight - parentRect.left - btnW - insetX - index * (btnW + gap);
    const topPx = screenTop - parentRect.top + insetY;
    btn.style.left = `${leftPx}px`;
    btn.style.top = `${topPx}px`;
  }

  // --- Pause -----------------------------------------------------------

  /**
   * Build the always-on PAUSE button. Sits at index 1 (left of MENU).
   */
  private buildPauseDomButton(): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = '⏸ PAUSE';
    btn.setAttribute('aria-label', 'Pause game');
    btn.style.position = 'absolute';
    btn.style.zIndex = '10';
    btn.style.padding = '0';
    btn.style.background = 'rgba(0,0,0,0.55)';
    btn.style.color = '#ffffff';
    btn.style.border = '1px solid rgba(255,255,255,0.25)';
    btn.style.borderRadius = '4px';
    btn.style.fontFamily = 'monospace';
    btn.style.fontWeight = 'bold';
    btn.style.cursor = 'pointer';
    btn.style.touchAction = 'manipulation';
    btn.style.pointerEvents = 'auto';
    btn.addEventListener('mouseenter', () => {
      btn.style.borderColor = 'rgba(255,255,255,0.6)';
    });
    btn.addEventListener('mouseleave', () => {
      btn.style.borderColor = 'rgba(255,255,255,0.25)';
    });
    btn.addEventListener('click', (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      this.pause();
    });

    const parent =
      (this.game.canvas.parentElement as HTMLElement | null) ??
      document.body;
    if (parent.style.position === '' || parent.style.position === 'static') {
      parent.style.position = 'relative';
    }
    parent.appendChild(btn);
    this.positionTopRightButton(btn, 1);
    return btn;
  }

  private positionPauseDomButton(): void {
    if (!this.pauseButtonDom) return;
    this.positionTopRightButton(this.pauseButtonDom, 1);
  }

  /**
   * Build the pause overlay: dimmed background + centered "PAUSED" title +
   * RESUME and MENU buttons. The two buttons are DOM elements for the same
   * reliability reasons as the share / corner buttons.
   */
  private buildPauseOverlay(): void {
    const w = VIEWPORT_PX_W;
    const h = VIEWPORT_PX_H;

    const bg = this.add.rectangle(w / 2, h / 2, w, h, 0x000000, 0.7);
    const title = this.add
      .text(w / 2, h / 2 - 60, 'PAUSED', {
        fontFamily: 'monospace',
        fontSize: '44px',
        fontStyle: 'bold',
        color: '#e8c87a',
      })
      .setOrigin(0.5);
    const hint = this.add
      .text(w / 2, h / 2 - 16, 'tap or press P to resume', {
        fontFamily: 'monospace',
        fontSize: '11px',
        color: '#888888',
      })
      .setOrigin(0.5);

    this.pauseOverlay = this.add.container(0, 0, [bg, title, hint]);
    this.pauseOverlay.setScrollFactor(0).setDepth(180).setVisible(false);

    // Background catches taps for "tap-anywhere to resume".
    bg.setInteractive();
    bg.on('pointerdown', () => {
      if (this.paused) this.resume();
    });

    // DOM buttons
    this.resumeButtonDom = this.buildPauseOverlayButton('▶ RESUME', '#2a8a4a', () => this.resume());
    this.pauseMenuButtonDom = this.buildPauseOverlayButton('☰ MENU', '#7a4a2a', () => {
      this.goToMenu();
    });
    this.positionPauseOverlayButtons();
    this.resumeButtonDom.style.display = 'none';
    this.pauseMenuButtonDom.style.display = 'none';
  }

  private buildPauseOverlayButton(label: string, bgColor: string, onClick: () => void): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = label;
    btn.style.position = 'absolute';
    btn.style.zIndex = '15';
    btn.style.padding = '0';
    btn.style.background = bgColor;
    btn.style.color = '#ffffff';
    btn.style.border = '2px solid rgba(255,255,255,0.25)';
    btn.style.borderRadius = '4px';
    btn.style.fontFamily = 'monospace';
    btn.style.fontSize = '15px';
    btn.style.fontWeight = 'bold';
    btn.style.cursor = 'pointer';
    btn.style.touchAction = 'manipulation';
    btn.style.pointerEvents = 'auto';
    btn.addEventListener('mouseenter', () => {
      btn.style.borderColor = 'rgba(255,255,255,0.6)';
    });
    btn.addEventListener('mouseleave', () => {
      btn.style.borderColor = 'rgba(255,255,255,0.25)';
    });
    btn.addEventListener('click', (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      onClick();
    });
    const parent =
      (this.game.canvas.parentElement as HTMLElement | null) ??
      document.body;
    if (parent.style.position === '' || parent.style.position === 'static') {
      parent.style.position = 'relative';
    }
    parent.appendChild(btn);
    return btn;
  }

  private positionPauseOverlayButtons(): void {
    if (!this.resumeButtonDom || !this.pauseMenuButtonDom) return;
    const canvas = this.game.canvas;
    const canvasRect = canvas.getBoundingClientRect();
    const parent = this.resumeButtonDom.offsetParent as HTMLElement | null;
    const parentRect = parent ? parent.getBoundingClientRect() : { left: 0, top: 0 };

    const displayScaleX = canvasRect.width / VIEWPORT_PX_W;
    const displayScaleY = canvasRect.height / VIEWPORT_PX_H;

    const btnW = 150 * displayScaleX;
    const btnH = 38 * displayScaleY;
    const gap = 14 * displayScaleY;

    const logicalCx = VIEWPORT_PX_W / 2;
    const screenCx = canvasRect.left + logicalCx * displayScaleX;
    const baseY = canvasRect.top + (VIEWPORT_PX_H / 2 + 30) * displayScaleY;

    for (const btn of [this.resumeButtonDom, this.pauseMenuButtonDom]) {
      btn.style.width = `${btnW}px`;
      btn.style.height = `${btnH}px`;
      btn.style.fontSize = `${15 * displayScaleY}px`;
    }

    const resumeTop = baseY;
    const menuTop = baseY + btnH + gap;
    this.resumeButtonDom.style.left = `${screenCx - parentRect.left - btnW / 2}px`;
    this.resumeButtonDom.style.top = `${resumeTop - parentRect.top}px`;
    this.pauseMenuButtonDom.style.left = `${screenCx - parentRect.left - btnW / 2}px`;
    this.pauseMenuButtonDom.style.top = `${menuTop - parentRect.top}px`;
  }

  /**
   * Pause the run. No-op if already paused, dead, won, or the result overlay
   * is showing — there's nothing useful to pause in those states.
   */
  pause(): void {
    if (this.paused) return;
    if (this.dead || this.won) return;
    if (this.resultOverlay && this.resultOverlay.visible) return;
    this.paused = true;
    // Freeze the water ripple while paused.
    for (const w of this.waterSprites) w.anims?.pause();
    if (this.pauseOverlay) {
      this.pauseOverlay.setVisible(true);
    }
    if (this.resumeButtonDom) {
      this.positionPauseOverlayButtons();
      this.resumeButtonDom.style.display = 'block';
    }
    if (this.pauseMenuButtonDom) {
      this.pauseMenuButtonDom.style.display = 'block';
    }
    // Confirm to the parent (suppressed if this pause originated from a
    // parent command — the bridge handles that).
    ParentBridge.notifyPauseChanged(true);
  }

  /** Resume the run. No-op if not paused. */
  resume(): void {
    if (!this.paused) return;
    this.paused = false;
    for (const w of this.waterSprites) w.anims?.resume();
    if (this.pauseOverlay) this.pauseOverlay.setVisible(false);
    if (this.resumeButtonDom) this.resumeButtonDom.style.display = 'none';
    if (this.pauseMenuButtonDom) this.pauseMenuButtonDom.style.display = 'none';
    ParentBridge.notifyPauseChanged(false);
  }

  // --- Pausable (ParentBridge) ------------------------------------------

  /**
   * Parent command: apply desired paused state. Idempotent and tolerant of
   * states where pausing is meaningless (dead/won/result overlay) — in those
   * cases a SET_PAUSE:true is simply ignored.
   */
  applyPauseFromParent(paused: boolean): void {
    if (paused) this.pause();
    else this.resume();
  }

  isPaused(): boolean {
    return this.paused;
  }

  /** Leave the run and return to the menu, clearing pause state first. */
  private goToMenu(): void {
    this.paused = false;
    this.scene.start('Menu');
  }

  /** Reset DOM share button to its idle SHARE state (between attempts). */
  private resetShareButtonDom(): void {
    this.shareButtonDom.textContent = 'SHARE';
    this.shareButtonDom.style.background = '#2a6ba6';
    this.shareButtonDom.disabled = false;
  }

  /** Handler for the share button. Builds share text + copies to clipboard. */
  private async onShareClick(): Promise<void> {
    // Hardened: any throw inside compose or copy is caught and surfaced as
    // a visible "Error" label, so silent failures don't leave the player
    // wondering whether their tap registered.
    let text: string;
    try {
      text = this.composeShareText();
    } catch (err) {
      console.error('splat-chimp: composeShareText failed', err);
      this.flashShareLabel('Error', '#a64a4a');
      return;
    }

    let ok = false;
    try {
      ok = await copyToClipboard(text);
    } catch (err) {
      console.error('splat-chimp: clipboard write failed', err);
    }
    if (ok) {
      this.flashShareLabel('Copied!', '#3da66b');
    } else {
      this.flashShareLabel('Copy failed', '#a66b3d');
    }
  }

  /** Flash the share button to a feedback state for a short period. */
  private flashShareLabel(text: string, bgCss: string): void {
    this.shareButtonDom.textContent = text;
    this.shareButtonDom.style.background = bgCss;
    this.time.delayedCall(1500, () => {
      if (this.shareButtonDom) this.resetShareButtonDom();
    });
  }

  /** Build the shareable text for the just-finished daily run. */
  private composeShareText(): string {
    const coconuts = this.playfield.coconuts.map((c, i) => ({
      invisible: c.invisible,
      collected: this.collected.has(i),
    }));
    const state = load();
    const streak = computeStreak(state);
    const summary = this.score.buildSummary(
      this.runElapsedMs,
      this.playfield.coconuts.length,
      this.won
    );
    return buildShareText({
      dateKey: this.cfg.seed,
      coconuts,
      totalCoconuts: this.playfield.coconuts.length,
      collectedCount: this.score.coconutsCollected,
      score: summary.total,
      elapsedMs: this.runElapsedMs,
      streak,
      complete: this.won,
    });
  }

  private tweenCamera(): void {
    if (this.cameraTween && this.cameraTween.isPlaying()) this.cameraTween.stop();
    const targetX = this.scroll.cameraTileX * TILE_SIZE;
    const targetY = this.scroll.cameraTileY * TILE_SIZE;
    this.cameraTween = this.tweens.add({
      targets: this.cameras.main,
      scrollX: targetX,
      scrollY: targetY,
      duration: CAMERA_TWEEN_MS,
      ease: 'Sine.easeOut',
    });
  }
}

// --- helpers --------------------------------------------------------------

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

function sign(v: number): number {
  return v > 0 ? 1 : v < 0 ? -1 : 0;
}
